/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useEffect, useState } from "react";
import ApiClient from "../../../services/apiClient";
import OtherIcon from "../../../assets/icons/contract/toggleable/IconContract";
import { useForm } from "../../../provider/Form/FormProvider";
import FormSubmitProps from "../../../types/FormSubmitProps";
import ContractLawCaseSelection from "./ContractLawCaseSelection";
import { AbstractCase, contractLawProductClassNames } from "../../../types/Entities/AbstractCase";
import { APPLICATION_STATUS_CONTRACT_RECOMMENDATION } from "../../../services/contractLaw/applicationStatus";
import { contractLawDefaultCaseOptions } from "../../../services/contractLaw/contractLawDefaultCaseOptions";

type ContractLawContractRecommendationProps = {
  submitProps: FormSubmitProps;
  product: AbstractCase;
  applicationStatus: number;
};

export const ContractLawContractRecommendation = ({ submitProps, product, applicationStatus }: ContractLawContractRecommendationProps) => {
  const [classifiedProductName, setClassifiedProductName] = useState<string | null>(null);
  const { updateValue, values } = useForm();

  useEffect(() => {
    /** Checking the application status prevents refetching classifiedProductName when switching case type and application form gets reused, initial with this form step set */
    if (classifiedProductName || product.applicationStatus !== APPLICATION_STATUS_CONTRACT_RECOMMENDATION) {
      return;
    }

    async function classifyProduct() {
      const response = await ApiClient.post("/services/ai_wizard/classify_product", {
        body: JSON.stringify({
          text: values.personalTextMessage,
        }),
      });

      let classifiedProductName = response?.[0].label;

      if (!contractLawProductClassNames.includes(classifiedProductName)) {
        classifiedProductName = "otherContract";
      }

      updateValue("name", classifiedProductName);
      setClassifiedProductName(classifiedProductName);
    }

    classifyProduct();
  }, [values, product]);

  let options = contractLawDefaultCaseOptions
    /** Changes other contract */
    .map((option) => {
      if (option.value === "otherContract") {
        return {
          labelText: "Sonstiger Vertrag",
          headline: "Sie haben das Gefühl, die von uns vorgeschlagene Vertragsart passt nicht?",
          labelIcon: <OtherIcon />,
          labelIconActive: <OtherIcon active />,
          value: "otherContract",
          optionHintText: "Jegliche andere Art von Vertrag, wie zum Beispiel mündliche Vereinbarungen, Leihgaben etc.",
        };
      }

      return option;
    })
    /** Only shows classified product option */
    .filter((option) => option.value === classifiedProductName || option.value === "otherContract");

  /** If options length is 1 the product could not be classified, changes otherContract option headline to none */
  if (options.length === 1 && options[0].value === "otherContract") {
    options = [
      {
        ...options[0],
        headline: undefined,
      },
    ];
  }

  return (
    <ContractLawCaseSelection
      product={product}
      isLoading={!classifiedProductName}
      submitProps={submitProps}
      options={options}
      applicationStatus={applicationStatus}
    />
  );
};
